import {inject, Injectable, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {TranslocoService} from '@jsverse/transloco';
import {StorageService} from './storage.service';
import {DOCUMENT} from '@angular/common';
import {logger} from './logger.service';

@Injectable({
	            providedIn: 'root'
            })
export class TranslateHelperService {
	private storageService: StorageService = inject(StorageService);
	private translateService: TranslocoService = inject(TranslocoService);
	private readonly document = inject(DOCUMENT, {optional: true}) as Document | null;
	private readonly supportedLangs = ['fr', 'en', 'it', 'de', 'es'];
	private readonly lang = signal<string>('en');
	public readonly language = this.lang.asReadonly();
	private readonly loadTimeoutMs = 5000;
	private ready: boolean = false;

	constructor() {
		const storedLang = this.storageService.get('currentLang');
		const urlLang = this.resolveUrlLang();
		const defaultLang = urlLang || storedLang || this.getBrowserLang() || 'en';
		this.lang.set(defaultLang);
		this.translateService.setActiveLang(defaultLang);
		if (urlLang && urlLang !== storedLang) {
			this.storageService.set('currentLang', urlLang);
		}
		this.translateService.events$.subscribe(event => {
			if (event.type === 'translationLoadSuccess' || event.type === 'translationLoadFailure') {
				this.ready = true;
			}
		});
	}

	get currentLang(): string {
		return this.lang() ?? this.translateService.getActiveLang() ?? this.getBrowserLang() ?? 'en';
	}

	/**
	 * Returns a translation for the given key, waiting until the translation files
	 * have been loaded. The method is asynchronous and resolves once a
	 * Transloco load event fires or the wait times out.
	 */
	public async instant(key: string): Promise<string> {
		try {
			if (!this.ready) {
				// Wait for the first successful load event
				await new Promise<void>((resolve) => {
					let sub: {unsubscribe: () => void} | null = null;
					const timeout = setTimeout(() => {
						this.ready = true;
						sub?.unsubscribe();
						resolve();
					}, this.loadTimeoutMs);
					sub = this.translateService.events$.subscribe(event => {
						if (event.type === 'translationLoadSuccess' || event.type === 'translationLoadFailure') {
							this.ready = true;
							clearTimeout(timeout);
							sub?.unsubscribe();
							resolve();
						}
					});
				});
			}
			return this.translateService.translate(key);
		} catch (error) {
			logger.error(error);
			throw error;
		}
	}

	public countryLabel(value: string | null | undefined): string {
		const raw = (value ?? '').trim();
		if (!raw) {
			return '';
		}
		const key = raw.startsWith('country.') ? raw : `country.${raw}`;
		const translated = this.translateService.translate(key);
		if (!translated || translated === key) {
			return raw;
		}
		return translated;
	}

	public localize(path: string): string {
		const lang = this.currentLang || this.getBrowserLang() || 'fr';
		const normalized = path ? (path.startsWith('/') ? path : `/${path}`) : '/';
		const firstSegment = normalized.split('/').filter(Boolean)[0];
		if (firstSegment && this.supportedLangs.includes(firstSegment)) {
			return normalized;
		}
		return normalized === '/' ? `/${lang}` : `/${lang}${normalized}`;
	}

	async switchLanguage(shortCode?: string) {
		if (shortCode === undefined || shortCode === this.lang()) {
			return;
		}
		this.ready = false;
		try {
			await firstValueFrom(this.translateService.load(shortCode));
		} catch (error) {
			logger.error(error);
		}
		this.lang.set(shortCode);
		this.translateService.setActiveLang(shortCode);
		this.storageService.set('currentLang', shortCode);
	}

	private getBrowserLang(): string | undefined {
		if (typeof navigator === 'undefined') {
			return undefined;
		}
		return navigator.language?.split('-')[0];
	}

	private resolveUrlLang(): string | undefined {
		const documentPath = this.document?.location?.pathname;
		const pathname = documentPath ?? (typeof window !== 'undefined' ? window.location.pathname : '');
		if (!pathname) {
			return undefined;
		}
		const firstSegment = pathname.split('/').filter(Boolean)[0];
		if (firstSegment && this.supportedLangs.includes(firstSegment)) {
			return firstSegment;
		}
		return undefined;
	}
}
